import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { LandingNavbarComponent } from '../../components/landing-navbar/landing-navbar.component';
import {
  LandingPageStats,
  LandingPageStatsService,
} from '../../modules/services/landing-page-stats.service';

type FaqItem = { question: string; answer: string };

type IntersectionObserverKey = "SCROLL_REVEAL" | "COUNTER";

@Component({
  selector: 'app-landing-page',
  standalone: true,
  imports: [CommonModule, RouterLink, LandingNavbarComponent],
  templateUrl: './landing-page.component.html',
  styleUrl: './landing-page.component.scss',
})
export class LandingPageComponent implements OnInit, AfterViewInit, OnDestroy {
  mobileMenuOpen = false;
  isScrolled = false;
  landingStats: LandingPageStats | null = null;

  readonly faqs: FaqItem[] = [
    {
      question: 'Who can register as a vendor on VMS?',
      answer:
        'Any registered business entity with a valid CAC number and tax identification can create a vendor profile. The platform supports sole proprietors, limited liability companies, and cooperatives.',
    },
    {
      question: 'How long does verification take?',
      answer:
        'Standard verification typically takes 3-5 business days. However, this may vary depending on the completeness of your documentation and current verification queue volume.',
    },
    {
      question: 'Does VMS integrate with our existing systems?',
      answer:
        'Yes, VMS offers REST API integrations and supports connection with ERP systems, accounting software, and existing government databases for seamless data exchange.',
    },
    {
      question: 'How is vendor data secured?',
      answer:
        'VMS employs bank-grade encryption (AES-256), secure cloud infrastructure, role-based access control, and regular security audits to protect all vendor and agency data.',
    },
    {
      question: 'What does VMS cost for agencies?',
      answer:
        'VMS is currently offered as a government-funded platform for public sector agencies. Vendor registration is free, with optional premium features available for enhanced visibility.',
    },
  ];

  openFaqIndex: number | null = 0;

  // private intersectionObserver: IntersectionObserver | null = null;

  private intersectionObserverMap: Map<IntersectionObserverKey, IntersectionObserver> = new Map();

  constructor(
    private readonly host: ElementRef<HTMLElement>,
    private readonly renderer: Renderer2,
    private readonly landingPageStatsService: LandingPageStatsService,
  ) {}

  ngOnInit(): void {
    this.landingPageStatsService.getLandingPageStats().subscribe({
      next: (stats) => {
        this.landingStats = stats;
        this.initCounterObserver();
      },
      error: () => {
        this.landingStats = null;
      },
    });
  }

  ngAfterViewInit(): void {
    const scrollRevealIntersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting)
            this.renderer.addClass(entry.target, 'visible');
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const revealEls =
      this.host.nativeElement.querySelectorAll('.scroll-reveal');
    revealEls.forEach((el) => scrollRevealIntersectionObserver?.observe(el));

    this.intersectionObserverMap.set("SCROLL_REVEAL", scrollRevealIntersectionObserver);
  }

  ngOnDestroy(): void {
    this.terminateIntersectionObservers();
  }

  initCounterObserver() {
    const counterIntersectionObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const targetElement = entry.target;
          if (entry.isIntersecting) {
            const targetText = targetElement.textContent;
            const parsedNumber = targetText.match(/\d+/g)[0];
            const targetCount = parseInt(parsedNumber);
            if (isNaN(targetCount)) continue;

            let currentCount = 0;
            const speed = 200;
            
            targetElement.textContent = targetElement.textContent.replace(/\d+/g, currentCount.toString());

            function updateCount() {
              const increment = targetCount / speed;
              currentCount = Math.ceil(++currentCount + increment);
              const parsedResult = targetElement.textContent.replace(/\d+/g, currentCount.toString());

              if (currentCount < targetCount) {
                targetElement.textContent = parsedResult;
                setTimeout(updateCount, 10);
              } else {
                targetElement.textContent = targetText;
              }
            }

            updateCount();
            counterIntersectionObserver.unobserve(targetElement);
          };
        }
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );

    const counterEls = this.host.nativeElement.querySelectorAll('.counter');
    counterEls.forEach(el => counterIntersectionObserver.observe(el));
  }

  terminateIntersectionObservers() {
    this.intersectionObserverMap.forEach((intersectionObserver, key, map) => {
      intersectionObserver?.disconnect();
      map.delete(key);
    });
  }

  toggleFaq(index: number): void {
    this.openFaqIndex = this.openFaqIndex === index ? null : index;
  }

  formatCompact(value: number | null | undefined): string {
    const safeValue = typeof value === 'number' ? value : 0;
    return new Intl.NumberFormat('en', {
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(safeValue);
  }
}
